import { useTranslations } from 'next-intl';
import type { FilterDimension, FilterValue } from '@/components/shared/sort-filter-bar';

export type MarketTypeFilterValue = 'influencer' | 'featured';

const MARKET_TYPE_ORDER: readonly MarketTypeFilterValue[] = ['influencer', 'featured'];

/** `marketIdentity` translator subset the filter needs. */
export type MarketTypeFilterTranslator = (
  key: 'filter' | 'filterInfluencer' | 'filterPlatform',
) => string;

interface MarketTypeFilterOptions {
  /** Platform (Featured) markets never pass through the proposal feed. */
  platform?: boolean;
}

/** The "Market type" filter dimension, translated for the current locale. */
export function useMarketTypeFilter(
  enabled: boolean,
  options: MarketTypeFilterOptions = {},
): FilterDimension[] {
  const t = useTranslations('marketIdentity');
  return buildMarketTypeFilter(enabled, t, options);
}

/** The "Market type" filter dimension with one chip per public designation. */
export function buildMarketTypeFilter(
  enabled: boolean,
  t: MarketTypeFilterTranslator,
  { platform = true }: MarketTypeFilterOptions = {},
): FilterDimension[] {
  if (!enabled) return [];
  const options = [{ value: 'influencer', label: t('filterInfluencer') }];
  if (platform) options.push({ value: 'featured', label: t('filterPlatform') });
  return [{ type: 'chipsMulti', key: 'marketType', label: t('filter'), options }];
}

/** Selected chips → the csv `marketType` SDK/URL param (stable order, deduped). */
export function marketTypeFilterParams(
  value: FilterValue | undefined,
  enabled: boolean,
): { marketType?: string } {
  if (!enabled || !Array.isArray(value)) return {};
  const selected = MARKET_TYPE_ORDER.filter((type) => value.some((item) => item === type));
  return selected.length > 0 ? { marketType: selected.join(',') } : {};
}

/** Parse a csv `?marketType=` URL param back into chip values, dropping unknowns. */
export function parseMarketTypeParam(
  raw: string | null,
  { platform = true }: MarketTypeFilterOptions = {},
): MarketTypeFilterValue[] {
  if (raw === null) return [];
  const parts = raw.split(',').map((part) => part.trim());
  return MARKET_TYPE_ORDER.filter(
    (type) => parts.includes(type) && (platform || type !== 'featured'),
  );
}
